import React, { FC, useState } from 'react'
import {
  Formik,
  FormikProps,
  FormikTouched,
  setNestedObjectValues,
} from 'formik'
import { Button, Heading } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  addTenantInputSchema,
  AddTenantInput,
  Contract,
  UserMetadata,
} from '../../../../common/validations'
import { validateFormValues } from '../../../utils'
import { FORM_FIELD_VALIDATION_ERROR_CLASSNAME } from '../../../components/FormField/FormFieldConstants'
import { AddTenantPreview } from './AddTenantPreview'
import { AddSingleTenant } from './components/AddSingleTenant'
import { AddTenantSuccess } from './AddTenantSuccess'
import { useContractPaths } from '../../../hooks/useContractPaths/useContractPaths'

type View = 'form' | 'preview' | 'success'

type Props = {
  contract: Contract
  userMetadata: UserMetadata
}

export const AddTenantForm: FC<Props> = ({ contract, userMetadata }) => {
  const { t } = useTranslation(['common', 'contractModification'])
  const navigate = useNavigate()
  const { terminateContractPath } = useContractPaths()
  const [view, setView] = useState<View>('form')

  const handleValidate = (values: AddTenantInput) => {
    const allValidationErrors = validateFormValues(values, addTenantInputSchema)
    return allValidationErrors
  }

  const handlePreview = async (form: FormikProps<AddTenantInput>) => {
    const errors = await form.validateForm()

    if (Object.keys(errors).length > 0) {
      // Mark fields with errors as touched to show error messages
      await form.setTouched(
        setNestedObjectValues<FormikTouched<AddTenantInput>>(errors, true)
      )
      document
        .querySelector(`.${FORM_FIELD_VALIDATION_ERROR_CLASSNAME}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } else {
      window.scrollTo(0, 0)
      setView('preview')
    }
  }

  const initialValues: AddTenantInput = {
    tenants: [
      {
        socialSecurityNumber: '',
        firstName: '',
        lastName: '',
      },
    ],
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => {}}
      validate={handleValidate}
    >
      {(form: FormikProps<AddTenantInput>) => (
        <>
          {view === 'success' && <AddTenantSuccess />}
          {view === 'preview' && (
            <AddTenantPreview
              formValues={form.values}
              contract={contract}
              cancel={() => setView('form')}
              onSuccess={() => setView('success')}
              userMetadata={userMetadata}
            />
          )}
          {view === 'form' && (
            <>
              <Heading element="h3" size="m" className="phone:font-medium">
                {t('contractModification:headings.addTenant')}
              </Heading>

              {form.values.tenants.map((_, index) => (
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <AddSingleTenant index={index} key={`single-tenant-${index}`} />
              ))}

              <Button
                variant="add"
                icon="plus-circle"
                className="mt-8"
                onClick={() => {
                  const newTenants = form.values.tenants
                  newTenants.push({
                    firstName: '',
                    lastName: '',
                    socialSecurityNumber: '',
                  })
                  form.setFieldValue('tenants', newTenants)
                }}
              >
                {t('contractModification:buttons.addPerson')}
              </Button>

              <div className="mt-8 flex items-center justify-end">
                <Button
                  type="button"
                  variant="cancel"
                  onClick={() => {
                    form.resetForm()
                    navigate(terminateContractPath)
                  }}
                >
                  {t('common:cancel')}
                </Button>

                <Button
                  type="button"
                  variant="primary"
                  onClick={() => handlePreview(form)}
                >
                  {t('common:preview')}
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </Formik>
  )
}
